import { Extension } from '@tiptap/core'

export const TextIndent = Extension.create({
  name: 'textIndent',

  addOptions () {
    return {
      types: ['paragraph', 'heading'],

      min: 0,
      max: 8,

      size: 2,
      unit: 'em'
    }
  },

  addGlobalAttributes () {
    const parseIndent = element => {
      const attr = element.getAttribute('data-indent')
      if (attr != null && attr !== '') {
        const n = Number(attr)
        return Number.isFinite(n) ? n : null
      }

      const ml = (element.style && element.style.marginLeft) ? element.style.marginLeft : ''
      if (!ml) return null

      const match = String(ml).trim().match(/^(-?\d+(?:\.\d+)?)(em|px)?$/)
      if (!match) return null

      const value = Number(match[1])
      const unit = match[2] || this.options.unit

      if (!Number.isFinite(value)) return null

      if (unit === this.options.unit) {
        return Math.round(value / this.options.size)
      }

      if (unit === 'px' && this.options.unit === 'em') {
        const em = value / 16
        return Math.round(em / this.options.size)
      }

      return Math.round(value / this.options.size)
    }

    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: null,
            parseHTML: element => parseIndent(element) ?? null,
            renderHTML: attributes => {
              const level = attributes.indent
              if (level == null || level === 0) {
                return {}
              }

              const clamped = Math.max(this.options.min, Math.min(this.options.max, level))
              const value = clamped * this.options.size

              return {
                'data-indent': String(clamped),
                style: `margin-left: ${value}${this.options.unit}`
              }
            }
          }
        }
      }
    ]
  },

  addKeyboardShortcuts () {
    const isTableContext = () => (
      this.editor.isActive('table') ||
      this.editor.isActive('tableCell') ||
      this.editor.isActive('tableHeader') ||
      this.editor.isActive('tableRow')
    )

    return {
      Tab: () => {
        if (isTableContext()) return false

        if (this.editor.isActive('listItem')) {
          return typeof this.editor.commands.sinkListItem === 'function'
            ? this.editor.commands.sinkListItem('listItem')
            : false
        }

        if (this.editor.isActive('taskItem')) {
          return typeof this.editor.commands.sinkListItem === 'function'
            ? this.editor.commands.sinkListItem('taskItem')
            : false
        }

        return this.editor.commands.indent()
      },
      'Shift-Tab': () => {
        if (isTableContext()) return false

        if (this.editor.isActive('listItem')) {
          return typeof this.editor.commands.liftListItem === 'function'
            ? this.editor.commands.liftListItem('listItem')
            : false
        }

        if (this.editor.isActive('taskItem')) {
          return typeof this.editor.commands.liftListItem === 'function'
            ? this.editor.commands.liftListItem('taskItem')
            : false
        }

        return this.editor.commands.outdent()
      }
    }
  },
  addCommands () {
    const clamp = level => Math.max(this.options.min, Math.min(this.options.max, level))
    const TABLE_NODE_NAMES = new Set(['table', 'tableRow', 'tableCell', 'tableHeader'])

    const isCellSelection = selection => Boolean(selection && (selection.$anchorCell || selection.$headCell))

    const hasTableAncestor = $pos => {
      if (!$pos) return false
      for (let depth = $pos.depth; depth > 0; depth -= 1) {
        const typeName = $pos.node(depth)?.type?.name
        if (TABLE_NODE_NAMES.has(typeName)) {
          return true
        }
      }
      return false
    }

    const isInTableContext = state => {
      const selection = state?.selection
      if (!selection) return false
      if (isCellSelection(selection)) return true
      return hasTableAncestor(selection.$from) || hasTableAncestor(selection.$to)
    }

    const applyToTypes = (commands, attrs) => {
      return this.options.types
        .map(type => commands.updateAttributes(type, attrs))
        .some(response => response)
    }

    const getCurrentLevel = state => {
      const { $from } = state.selection
      const level = $from.parent?.attrs?.indent
      return typeof level === 'number' ? level : 0
    }

    return {
      setIndent:
        level =>
          ({ state, commands }) => {
            if (isInTableContext(state)) return false
            const next = clamp(Number(level) || 0)
            return applyToTypes(commands, { indent: next || null })
          },

      unsetIndent:
        () =>
          ({ state, commands }) => {
            if (isInTableContext(state)) return false
            return applyToTypes(commands, { indent: null })
          },

      indent:
        () =>
          ({ state, commands }) => {
            if (isInTableContext(state)) return false
            const current = getCurrentLevel(state)
            const next = clamp(current + 1)
            return applyToTypes(commands, { indent: next || null })
          },

      outdent:
        () =>
          ({ state, commands }) => {
            if (isInTableContext(state)) return false
            const current = getCurrentLevel(state)
            const next = clamp(current - 1)
            return applyToTypes(commands, { indent: next || null })
          }
    }
  }
})
